import { useAuth } from "react-oidc-context";
import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "/api";

const getDefaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  const toYmd = (date) => date.toISOString().slice(0, 10);
  return {
    start: toYmd(start),
    end: toYmd(end),
  };
};

const cloneByDay = (source) =>
  Object.fromEntries(Object.entries(source).map(([day, list]) => [day, [...list]]));

const cloneEvents = (source) =>
  Object.fromEntries(Object.entries(source).map(([day, map]) => [day, { ...map }]));

export const useGlobalStaysFeed = ({ pageSize = 50, status } = {}) => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const [dateRange, setDateRange] = useState(getDefaultRange);
  const [orderedDays, setOrderedDays] = useState([]);
  const [staysByDay, setStaysByDay] = useState({});
  const [eventsByDayStay, setEventsByDayStay] = useState({});

  const staysByDayRef = useRef(staysByDay);
  const eventsByDayStayRef = useRef(eventsByDayStay);

  useEffect(() => {
    staysByDayRef.current = staysByDay;
  }, [staysByDay]);

  useEffect(() => {
    eventsByDayStayRef.current = eventsByDayStay;
  }, [eventsByDayStay]);

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState(null);

  const mergeStays = useCallback((stays, reset) => {
    if (!Array.isArray(stays) || stays.length === 0) {
      if (reset) {
        setStaysByDay({});
        setEventsByDayStay({});
        setOrderedDays([]);
      }
      return;
    }

    const nextByDay = reset ? {} : cloneByDay(staysByDayRef.current);
    const nextEvents = reset ? {} : cloneEvents(eventsByDayStayRef.current);

    stays.forEach((stay) => {
      const dayKey = stay.stayDay || stay.checkIn || stay.checkOut || stay.endDate || "";
      if (!dayKey) return;

      nextByDay[dayKey] ??= [];
      nextByDay[dayKey] = nextByDay[dayKey].filter((item) => item.sId !== stay.sId);
      nextByDay[dayKey].push(stay);
      nextByDay[dayKey].sort((a, b) => new Date(b.checkIn || b.stayDay) - new Date(a.checkIn || a.stayDay));

      nextEvents[dayKey] ??= {};
      nextEvents[dayKey][stay.sId] = Array.isArray(stay.events) ? stay.events : [];
    });

    setStaysByDay(nextByDay);
    setEventsByDayStay(nextEvents);
    setOrderedDays(Object.keys(nextByDay).sort((a, b) => new Date(b) - new Date(a)));
  }, []);

  const fetchPage = useCallback(
    async ({ cursorDate, cursorId, reset = false } = {}) => {
      if (!token) return;

      setError(null);
      if (reset) {
        setIsInitialLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const headers = { Authorization: `Bearer ${token}` };
        const params = { limit: pageSize };
        if (dateRange.start) params.startDate = dateRange.start;
        if (dateRange.end) params.endDate = dateRange.end;
        if (cursorDate && cursorId) {
          params.cursorDate = cursorDate;
          params.cursorId = cursorId;
        }
        if (status) {
          params.status = status;
        }

        const response = await axios.get(`${API_URL}/greenmiles/stays`, { headers, params });
        const payload = response.data?.data || {};
        const stays = Array.isArray(payload.stays) ? payload.stays : [];

        setCursor(payload.nextCursor || null);
        setHasMore(Boolean(payload.nextCursor));
        mergeStays(stays, reset);
      } catch (err) {
        console.error("Erreur lors du chargement des séjours globaux:", err);
        setError(err.response?.data?.message || "Impossible de charger les séjours");
      } finally {
        if (reset) {
          setIsInitialLoading(false);
        } else {
          setIsFetchingMore(false);
        }
      }
    },
    [API_URL, dateRange.end, dateRange.start, mergeStays, pageSize, status, token]
  );

  const resetAndFetch = useCallback(() => {
    setCursor(null);
    setHasMore(true);
    fetchPage({ reset: true });
  }, [fetchPage]);

  useEffect(() => {
    if (!token) return;
    resetAndFetch();
  }, [resetAndFetch, token]);

  const loadMore = useCallback(() => {
    if (!hasMore || isInitialLoading || isFetchingMore || !cursor) return;
    fetchPage({ cursorDate: cursor.cursorDate, cursorId: cursor.cursorId, reset: false });
  }, [cursor, fetchPage, hasMore, isFetchingMore, isInitialLoading]);

  const observerRef = useRef(null);
  const loaderRef = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      });
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [loadMore]
  );

  return {
    orderedDays,
    staysByDay,
    eventsByDayStay,
    dateRange,
    setDateRange,
    isInitialLoading,
    isFetchingMore,
    error,
    hasMore,
    loaderRef,
    refetch: resetAndFetch,
  };
};

